<script setup lang="ts">
import { computed, ref, type ComponentInstance } from 'vue';
import { useLoaders, useTaskLoader } from '@/composeables';
import { useBibAssignStore, useBibAssignStoreRef, useToastStore } from '@/stores';
import { DropdownItemButton } from '@/objects';
import { ButtonComp, ContentComp, DropdownComp, ModalComp } from '@/components';
import BibAssignWizardView from './BibAssignWizardView.vue';

const bibAssignWizard = ref<ComponentInstance<typeof BibAssignWizardView> | null>(null);

const { AddLoader } = useTaskLoader();
useLoaders(AddLoader, ['CATEGORIES', 'GROUPS', 'CONTESTANTS', 'BIBASSIGN']);

const { BibAssigns } = useBibAssignStoreRef();
const { ReloadBibAssigns } = useBibAssignStore();

const canAssign = ref<boolean>(false);

// If there are any changes that need to be saved
const hasChanges = ref<boolean>(false);

// This is set if the current page is the table page
const isTablePage = ref<boolean>(false);

// This is set if the current page is the seeding page
const isSeedingPage = ref<boolean>(false);

// pages we have been to to support back button
const pageHistory = ref<string[]>([]);

// Helper property to know if we need to show the back button
const hasBack = computed<boolean>(() => {
    return pageHistory.value.length > 0;
});

const splitAction = DropdownItemButton.create({
    action: () => bibAssignWizard.value?.showPreview(),
    icon: 'fa-magnifying-glass',
    translationkey: 'bibassign.previewtable',
});

const dropdownItems = [
    DropdownItemButton.create({
        action: () => bibAssignWizard.value?.printPreviewReport(),
        translationkey: 'bibassign.previewreport',
        icon: 'fa-file-pdf',
    }),
];

/**
 * This will show a message box to ask for confirmation on assigning bibs.
 */
const assignBibs = async () => {
    if (bibAssignWizard.value == null) {
        return;
    }

    bibAssignWizard.value?.openAssignDialog();
};

/**
 * Saves the bib assign configuration.
 */
const saveBibAssign = async (showToast = true, closeDialog = true) => {
    if (bibAssignWizard.value == null) {
        return;
    }

    try {
        await bibAssignWizard.value.save();
        if (showToast) {
            useToastStore().addSuccess('bibassign.saved');
        }

        if (closeDialog) {
            modal.value?.hide();
        }
    } catch (error) {
        if (showToast) {
            useToastStore().addError('bibassign.saveerror');
        } else {
            throw error;
        }
    }
};
const modal = ref<ComponentInstance<typeof ModalComp> | null>(null);
/**
 * Helper function the check if there are bib assign rules for the event.
 */
const hasBibAssignRules = computed<boolean | null>(() => {
    return BibAssigns.value.length > 0;
});

const assigned = () => {
    emit('assigned');
    ReloadBibAssigns();
};

const emit = defineEmits<{
    (e: 'assigned'): void;
}>();

defineExpose({
    show: () => modal.value?.show(),
    hide: () => modal.value?.hide(),
});
</script>
<template>
    <div>
        <ModalComp
            ref="modal"
            title="dashboard.assignbibs"
            size="xxl"
            :close-on-escape="false"
        >
            <template #exitButton> </template>
            <template #content>
                <ContentComp
                    :fill-content="true"
                    class="p-0"
                    :height="66"
                >
                    <BibAssignWizardView
                        ref="bibAssignWizard"
                        :hasBibAssignRules="hasBibAssignRules"
                        @assignable="(state: boolean) => (canAssign = state)"
                        @isTablePage="(state: boolean) => (isTablePage = state)"
                        @isSeedingPage="(state: boolean) => (isSeedingPage = state)"
                        @hasChanges="(state: boolean) => (hasChanges = state)"
                        @assigned="assigned"
                        @leftPage="
                            (page: string) => {
                                pageHistory.push(page);
                            }
                        "
                    />
                </ContentComp>
            </template>

            <template #footer>
                <ButtonComp
                    icon="fa-arrow-left"
                    v-if="hasBack"
                    button-type="Secondary"
                    @click="bibAssignWizard?.goto(pageHistory.pop()!)"
                    >{{ $tm('general.back') }}</ButtonComp
                >
                <div class="flex-fill"></div>
                <ButtonComp
                    button-type="Secondary"
                    @click="modal?.hide()"
                    >{{ $tm('button.close') }}</ButtonComp
                >
                <ButtonComp
                    v-if="isTablePage"
                    button-type="Info"
                    @click="bibAssignWizard?.gotoSeeding()"
                    >{{ $tm('bibassign.seeding') }}</ButtonComp
                >
                <ButtonComp
                    v-if="isSeedingPage"
                    button-type="Info"
                    @click="bibAssignWizard?.gotoTable()"
                    >{{ $tm('bibassign.bibassign') }}</ButtonComp
                >
                <DropdownComp
                    v-if="canAssign"
                    translationkey="Input with text"
                    :dropdownItems="dropdownItems"
                    :splitAction="splitAction"
                />
                <ButtonComp
                    button-type="Save"
                    :disabled="!canAssign || !hasChanges"
                    @click="saveBibAssign"
                    >{{ $tm('button.save') }}</ButtonComp
                >
                <ButtonComp
                    button-type="Primary"
                    :disabled="!canAssign"
                    @click="assignBibs"
                    >{{ $tm('bibassign.assign') }}</ButtonComp
                >
            </template>
        </ModalComp>
    </div>
</template>
